import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { FileSpreadsheet, FileDown, Loader2 } from 'lucide-react';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { api } from '../api/client.js';
import { useSession } from '../auth/SessionContext.jsx';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from 'recharts';

const PIE_COLORS = [
  '#1A2849', '#2EB6A7', '#5d6677', '#8b9bb4', '#c0392b',
  '#d4a373', '#6b8e7f', '#a07c8a', '#3b4456', '#a8b4c9',
];

function buildQuery(filters) {
  const params = [];
  if (filters.city) params.push(`city=${encodeURIComponent(filters.city)}`);
  if (filters.from) params.push(`from=${encodeURIComponent(filters.from)}`);
  if (filters.to) params.push(`to=${encodeURIComponent(filters.to)}`);
  if (filters.client_code_id) params.push(`client_code_id=${encodeURIComponent(filters.client_code_id)}`);
  return params.length > 0 ? '?' + params.join('&') : '';
}

export default function AdminStats() {
  const { session } = useSession();

  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    city: '',
    from: '',
    to: '',
    client_code_id: '',
  });

  const [debounceTimer, setDebounceTimer] = useState(null);
  const [xlsxExporting, setXlsxExporting] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);

  const onExportXlsx = async () => {
    if (xlsxExporting) return;
    setXlsxExporting(true);
    setError(null);
    try {
      await api.download(
        `/api/admin/stats/export.xlsx${buildQuery(filters)}`,
        'vertuoso-statistiques.xlsx'
      );
    } catch (e) {
      setError(`Export Excel : ${e.message || 'erreur'}`);
    } finally {
      setXlsxExporting(false);
    }
  };

  const onExportPdf = async () => {
    if (pdfExporting) return;
    setPdfExporting(true);
    setError(null);
    try {
      await api.download(
        `/api/admin/stats/export.pdf${buildQuery(filters)}`,
        'vertuoso-statistiques.pdf'
      );
    } catch (e) {
      setError(`Export PDF : ${e.message || 'erreur'}`);
    } finally {
      setPdfExporting(false);
    }
  };

  if (session && session.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  useEffect(() => {
    const loadClients = async () => {
      try {
        const res = await api.get('/api/admin/codes');
        setClients(Array.isArray(res?.items) ? res.items : []);
      } catch (e) {
        console.error('Erreur chargement clients:', e);
      }
    };
    loadClients();
  }, []);

  const fetchStats = async (filtersToUse = filters) => {
    setLoading(true);
    setError(null);
    try {
      const query = buildQuery(filtersToUse);
      const res = await api.get(`/api/admin/stats${query}`);
      setData(res);
    } catch (e) {
      setError(e.message || 'Erreur de chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);

    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => {
      fetchStats(newFilters);
    }, 250);
    setDebounceTimer(timer);
  };

  const onResetFilters = () => {
    const newFilters = { city: '', from: '', to: '', client_code_id: '' };
    setFilters(newFilters);
    if (debounceTimer) clearTimeout(debounceTimer);
    fetchStats(newFilters);
  };

  const cityOptions = data?.by_city?.map(c => c.city) || [];
  const today = new Date();
  const formattedDate = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;

  return (
    <div className="stats-page">
      <Header />

      <main className="stats-main">
        {/* Header section */}
        <div className="stats-header">
          <div className="stats-header-left">
            <h1 className="stats-title">Rapport Statistiques Avaloirs</h1>
            <p className="stats-subtitle">Vertuoso · généré le {formattedDate} · {data?.total || 0} avaloirs analysés</p>
          </div>
          <div className="stats-header-right">
            <button
              type="button"
              onClick={onExportXlsx}
              disabled={xlsxExporting}
              className="stats-btn-export"
              title={xlsxExporting ? 'Génération Excel en cours…' : 'Exporter Excel'}
              aria-label={xlsxExporting ? 'Génération Excel en cours' : 'Exporter Excel'}
            >
              {xlsxExporting
                ? <Loader2 size={18} style={{ animation: 'vrt-spin-kf 1s linear infinite' }} />
                : <FileSpreadsheet size={18} />}
              {xlsxExporting ? 'Excel…' : 'Excel'}
            </button>
            <button
              type="button"
              onClick={onExportPdf}
              disabled={pdfExporting}
              className="stats-btn-export"
              title={pdfExporting ? 'Génération PDF en cours (peut prendre 5-30 s)…' : 'Exporter PDF'}
              aria-label={pdfExporting ? 'Génération PDF en cours' : 'Exporter PDF'}
            >
              {pdfExporting
                ? <Loader2 size={18} style={{ animation: 'vrt-spin-kf 1s linear infinite' }} />
                : <FileDown size={18} />}
              {pdfExporting ? 'PDF…' : 'PDF'}
            </button>
          </div>
        </div>

        {error && (
          <div className="stats-error" role="alert">
            Erreur : {error}
          </div>
        )}

        {/* Filters */}
        <div className="stats-filters">
          <label className="stats-filter-item">
            <span className="stats-filter-label">Ville</span>
            <select
              value={filters.city}
              onChange={(e) => handleFilterChange('city', e.target.value)}
              className="stats-filter-input"
            >
              <option value="">Toutes</option>
              {cityOptions.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </label>

          <label className="stats-filter-item">
            <span className="stats-filter-label">Client</span>
            <select
              value={filters.client_code_id}
              onChange={(e) => handleFilterChange('client_code_id', e.target.value)}
              className="stats-filter-input"
            >
              <option value="">Tous</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.client_name}</option>
              ))}
            </select>
          </label>

          <label className="stats-filter-item">
            <span className="stats-filter-label">Du</span>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => handleFilterChange('from', e.target.value)}
              className="stats-filter-input"
            />
          </label>

          <label className="stats-filter-item">
            <span className="stats-filter-label">Au</span>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => handleFilterChange('to', e.target.value)}
              className="stats-filter-input"
            />
          </label>

          <button type="button" className="stats-btn-reset" onClick={onResetFilters}>
            Réinitialiser
          </button>
        </div>

        {loading ? (
          <div className="stats-empty">Chargement des statistiques…</div>
        ) : !data ? (
          <div className="stats-empty">Aucune donnée disponible.</div>
        ) : (
          <>
            {/* KPIs */}
            <div className="stats-kpis">
              <div className="stats-kpi">
                <div className="stats-kpi-value">{data.total}</div>
                <div className="stats-kpi-label">Total</div>
              </div>

              <div className="stats-kpi-separator" />

              <div className="stats-kpi">
                <div className="stats-kpi-value" style={{ color: '#c0392b' }}>
                  {data.kpis.priority_life?.count ?? 0}
                </div>
                <div className="stats-kpi-label">Priorité Life</div>
                <div className="stats-kpi-pct">({(data.kpis.priority_life?.pct ?? 0).toFixed(1)}%)</div>
              </div>

              <div className="stats-kpi-separator" />

              <div className="stats-kpi">
                <div className="stats-kpi-value">{data.kpis.bouches?.count ?? 0}</div>
                <div className="stats-kpi-label">Bouchés</div>
                <div className="stats-kpi-pct">({(data.kpis.bouches?.pct ?? 0).toFixed(1)}%)</div>
              </div>

              <div className="stats-kpi-separator" />

              <div className="stats-kpi">
                <div className="stats-kpi-value">{data.kpis.non_adaptes?.count ?? 0}</div>
                <div className="stats-kpi-label">Non adaptés</div>
                <div className="stats-kpi-pct">({(data.kpis.non_adaptes?.pct ?? 0).toFixed(1)}%)</div>
              </div>

              <div className="stats-kpi-separator" />

              <div className="stats-kpi">
                <div className="stats-kpi-value">{data.kpis.raccordable_vertuoso?.count ?? 0}</div>
                <div className="stats-kpi-label">Raccordables</div>
                <div className="stats-kpi-pct">({(data.kpis.raccordable_vertuoso?.pct ?? 0).toFixed(1)}%)</div>
              </div>
            </div>

            {/* Questions section */}
            {data.by_question && data.by_question.length > 0 && (
              <section className="stats-section">
                <h2 className="stats-section-title">Répartition des réponses</h2>
                <p className="stats-section-desc">Une représentation visuelle pour chaque question du formulaire.</p>
                <div className="stats-pie-grid">
                  {data.by_question.map((q, idx) => (
                    <QuestionCard key={q.question_id || idx} question={q} />
                  ))}
                </div>
              </section>
            )}

            {/* Text responses section */}
            {data.by_question && data.by_question.filter(q => ['text', 'textarea', 'number'].includes(q.type)).length > 0 && (
              <section className="stats-section">
                <h2 className="stats-section-title">Réponses libres</h2>
                <div className="stats-text-grid">
                  {data.by_question
                    .filter(q => ['text', 'textarea', 'number'].includes(q.type))
                    .map((q, idx) => (
                      <div key={q.question_id || idx} className="stats-text-card">
                        <h3 className="stats-text-title">{q.label}</h3>
                        <div className="stats-text-counts">
                          {q.answered_count} répondus · {q.no_answer_count} sans réponse
                        </div>
                        <div className="stats-progress-bar">
                          <div
                            className="stats-progress-fill"
                            style={{
                              width: `${((q.answered_count || 0) / ((q.answered_count || 0) + (q.no_answer_count || 0)) * 100) || 0}%`
                            }}
                          />
                        </div>
                      </div>
                    ))}
                </div>
              </section>
            )}

            {/* Timeline */}
            {data.timeline && data.timeline.length > 0 && (
              <section className="stats-section">
                <h2 className="stats-section-title">Activité terrain</h2>
                <p className="stats-section-desc">Avaloirs ajoutés au registre, jour par jour.</p>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.timeline} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--vrt-border)" />
                    <XAxis dataKey="date" style={{ fontSize: 12 }} />
                    <YAxis style={{ fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{
                        background: '#fff',
                        border: '1px solid var(--vrt-border)',
                        borderRadius: '4px',
                        padding: '8px'
                      }}
                    />
                    <Bar dataKey="count" fill="#2EB6A7" name="Total" />
                    <Bar dataKey="priority_count" fill="#c0392b" name="Priorité Life" />
                  </BarChart>
                </ResponsiveContainer>
              </section>
            )}

            {/* Geographic section */}
            <section className="stats-section">
              <h2 className="stats-section-title">Répartition géographique</h2>
              <div className="stats-geo-grid">
                {/* Top cities */}
                <div className="stats-geo-card">
                  <h3 className="stats-geo-title">Par ville</h3>
                  <table className="stats-geo-table">
                    <thead>
                      <tr>
                        <th>Ville</th>
                        <th>Nombre</th>
                        <th>Part</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.by_city || []).slice(0, 10).map((row, idx) => {
                        const totalCount = data.total || 1;
                        const pct = ((row.count / totalCount) * 100).toFixed(1);
                        return (
                          <tr key={idx}>
                            <td className="stats-table-city">{row.city}</td>
                            <td className="stats-table-count">{row.count}</td>
                            <td className="stats-table-pct">{pct}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* By client */}
                <div className="stats-geo-card">
                  <h3 className="stats-geo-title">Par client</h3>
                  <table className="stats-geo-table">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th>Nombre</th>
                        <th>Part</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.by_client || []).slice(0, 10).map((row, idx) => {
                        const totalCount = data.total || 1;
                        const pct = ((row.count / totalCount) * 100).toFixed(1);
                        return (
                          <tr key={idx}>
                            <td className="stats-table-city">{row.client_name}</td>
                            <td className="stats-table-count">{row.count}</td>
                            <td className="stats-table-pct">{pct}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>

            {/* Footer */}
            <div className="stats-footer">
              Rapport généré automatiquement par Vertuoso Carto. Données mises à jour en temps réel.
            </div>
          </>
        )}
      </main>

      <Footer />

      <style>{`
        @keyframes vrt-spin-kf { to { transform: rotate(360deg); } }
        .stats-page {
          min-height: 100vh;
          background: #fafbfc;
        }

        .stats-main {
          max-width: 1400px;
          margin: 0 auto;
          padding: 32px 16px 60px;
        }

        .pie-card {
          padding: 16px;
          border: 1px solid var(--vrt-border);
          border-radius: 8px;
          background: #fff;
        }

        .pie-title {
          margin: 0 0 4px;
          font-size: 13px;
          font-weight: 600;
          color: var(--vrt-navy);
          line-height: 1.4;
        }

        .pie-meta {
          margin: 0 0 12px;
          font-size: 11px;
          color: var(--vrt-text-muted);
        }

        .pie-legend {
          margin-top: 12px;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .pie-legend-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
        }

        .pie-legend-color {
          width: 12px;
          height: 12px;
          border-radius: 2px;
          flex-shrink: 0;
        }

        .pie-legend-label {
          flex: 1;
          color: var(--vrt-navy);
          font-weight: 500;
        }

        .pie-legend-count {
          color: var(--vrt-text-muted);
          font-weight: 600;
          min-width: 30px;
          text-align: right;
        }

        .pie-legend-pct {
          color: var(--vrt-text-muted);
          min-width: 40px;
          text-align: right;
        }

        .pie-multiselect-note {
          margin: 8px 0 0;
          font-size: 10px;
          color: var(--vrt-text-muted);
          font-style: italic;
        }

        /* Header */
        .stats-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 28px;
          gap: 20px;
          flex-wrap: wrap;
        }

        .stats-header-left {
          flex: 1;
          min-width: 200px;
        }

        .stats-header-right {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .stats-title {
          margin: 0 0 8px;
          font-size: 28px;
          font-weight: 700;
          color: var(--vrt-navy);
          line-height: 1.2;
        }

        .stats-subtitle {
          margin: 0;
          font-size: 14px;
          color: var(--vrt-text-muted);
          line-height: 1.5;
        }

        .stats-btn-export {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          border: 1px solid var(--vrt-border);
          border-radius: 6px;
          background: transparent;
          color: var(--vrt-navy);
          font-size: 13px;
          font-weight: 600;
          text-decoration: none;
          cursor: pointer;
          transition: all 0.2s;
        }

        .stats-btn-export:hover:not(:disabled) {
          background: var(--vrt-bg-alt);
        }
        .stats-btn-export:disabled {
          opacity: 0.7;
          cursor: progress;
        }

        /* Filters */
        .stats-filters {
          display: flex;
          gap: 12px;
          align-items: flex-end;
          flex-wrap: wrap;
          padding: 16px;
          background: rgba(var(--vrt-border-rgb, 227, 231, 238), 0.3);
          border: 1px solid var(--vrt-border);
          border-radius: 8px;
          margin-bottom: 28px;
        }

        .stats-filter-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 140px;
          flex: 1;
        }

        .stats-filter-label {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--vrt-navy);
        }

        .stats-filter-input {
          padding: 8px 10px;
          border: 1px solid var(--vrt-border);
          border-radius: 6px;
          font-size: 13px;
          background: #fff;
          color: var(--vrt-text);
          font-family: inherit;
        }

        .stats-filter-input:focus {
          outline: 2px solid var(--vrt-teal);
          outline-offset: 1px;
        }

        .stats-btn-reset {
          padding: 8px 12px;
          border: 1px solid var(--vrt-border);
          border-radius: 6px;
          background: transparent;
          color: var(--vrt-navy);
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .stats-btn-reset:hover {
          background: var(--vrt-bg-alt);
        }

        /* KPIs */
        .stats-kpis {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
          padding: 24px 0;
          margin-bottom: 28px;
          border-bottom: 1px solid var(--vrt-border);
        }

        .stats-kpi {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          flex: 1;
          min-width: 90px;
        }

        .stats-kpi-value {
          font-size: 32px;
          font-weight: 700;
          color: var(--vrt-navy);
          line-height: 1;
          margin-bottom: 4px;
        }

        .stats-kpi-label {
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--vrt-text-muted);
          margin-bottom: 2px;
        }

        .stats-kpi-pct {
          font-size: 12px;
          color: var(--vrt-text-muted);
        }

        .stats-kpi-separator {
          width: 1px;
          height: 60px;
          background: var(--vrt-border);
          align-self: center;
        }

        /* Sections */
        .stats-section {
          margin-bottom: 32px;
          padding-bottom: 28px;
          border-bottom: 1px solid var(--vrt-border);
        }

        .stats-section:last-child {
          border-bottom: none;
          margin-bottom: 20px;
        }

        .stats-section-title {
          margin: 0 0 8px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--vrt-navy);
        }

        .stats-section-desc {
          margin: 0 0 16px;
          font-size: 13px;
          color: var(--vrt-text-muted);
          font-style: italic;
        }

        /* Pie grid */
        .stats-pie-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 24px;
        }

        /* Text grid */
        .stats-text-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 24px;
        }

        .stats-text-card {
          padding: 16px;
          border: 1px solid var(--vrt-border);
          border-radius: 8px;
          background: #fff;
        }

        .stats-text-title {
          margin: 0 0 8px;
          font-size: 13px;
          font-weight: 600;
          color: var(--vrt-navy);
        }

        .stats-text-counts {
          margin: 0 0 8px;
          font-size: 12px;
          color: var(--vrt-text-muted);
        }

        .stats-progress-bar {
          width: 100%;
          height: 6px;
          background: var(--vrt-bg-alt);
          border-radius: 3px;
          overflow: hidden;
        }

        .stats-progress-fill {
          height: 100%;
          background: var(--vrt-teal);
          border-radius: 3px;
        }

        /* Geographic section */
        .stats-geo-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 24px;
        }

        .stats-geo-card {
          border: 1px solid var(--vrt-border);
          border-radius: 8px;
          padding: 16px;
          background: #fff;
        }

        .stats-geo-title {
          margin: 0 0 12px;
          font-size: 13px;
          font-weight: 600;
          color: var(--vrt-navy);
        }

        .stats-geo-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }

        .stats-geo-table th,
        .stats-geo-table td {
          padding: 8px;
          text-align: left;
          border-bottom: 1px solid var(--vrt-border);
        }

        .stats-geo-table thead th {
          background: var(--vrt-bg-alt);
          color: var(--vrt-navy);
          font-weight: 700;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .stats-table-city {
          font-weight: 600;
          color: var(--vrt-navy);
        }

        .stats-table-count {
          text-align: center;
          color: var(--vrt-navy);
          font-weight: 600;
        }

        .stats-table-pct {
          text-align: right;
          color: var(--vrt-text-muted);
          font-size: 11px;
        }

        /* Error and loading */
        .stats-error {
          background: #fdecea;
          color: #a23123;
          padding: 12px 14px;
          border-radius: 8px;
          margin-bottom: 16px;
          font-size: 13px;
        }

        .stats-empty {
          padding: 40px;
          text-align: center;
          color: var(--vrt-text-muted);
          background: var(--vrt-bg-alt);
          border-radius: 8px;
          font-size: 14px;
        }

        /* Footer */
        .stats-footer {
          text-align: center;
          padding: 20px;
          font-size: 12px;
          color: var(--vrt-text-muted);
          border-top: 1px solid var(--vrt-border);
          margin-top: 32px;
        }

        /* Mobile */
        @media (max-width: 768px) {
          .stats-main {
            padding: 20px 12px 40px;
          }

          .stats-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .stats-header-right {
            justify-content: flex-start;
            width: 100%;
          }

          .stats-title {
            font-size: 22px;
          }

          .stats-filters {
            flex-direction: column;
          }

          .stats-filter-item {
            width: 100%;
            min-width: unset;
          }

          .stats-btn-reset {
            width: 100%;
          }

          .stats-kpis {
            flex-direction: column;
          }

          .stats-kpi-separator {
            display: none;
          }

          .stats-pie-grid,
          .stats-text-grid {
            grid-template-columns: 1fr;
          }

          .stats-geo-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}

function QuestionCard({ question }) {
  if (question.type === 'select') {
    const hasOptions = question.options && question.options.length > 0;
    const chartData = hasOptions ? question.options.map(opt => ({
      name: opt.label,
      count: opt.count,
    })) : [];

    return (
      <div className="pie-card">
        <h3 className="pie-title">{question.label}</h3>
        <p className="pie-meta">{question.answered_count} répondus / {question.no_answer_count} sans réponse</p>
        {chartData.length > 0 && (
          <>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  innerRadius={0}
                  dataKey="count"
                  nameKey="name"
                  label={false}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="pie-legend">
              {question.options.map((opt, i) => (
                <div key={i} className="pie-legend-item">
                  <span
                    className="pie-legend-color"
                    style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                  />
                  <span className="pie-legend-label">{opt.label}</span>
                  <span className="pie-legend-count">{opt.count}</span>
                  <span className="pie-legend-pct">{(opt.pct || 0).toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }

  if (question.type === 'multiselect') {
    const hasOptions = question.options && question.options.length > 0;
    const chartData = hasOptions ? question.options.map(opt => ({
      name: opt.label,
      count: opt.count,
    })) : [];

    return (
      <div className="pie-card">
        <h3 className="pie-title">{question.label} *</h3>
        <p className="pie-meta">{question.answered_count} répondus / {question.no_answer_count} sans réponse</p>
        {chartData.length > 0 && (
          <>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  innerRadius={0}
                  dataKey="count"
                  nameKey="name"
                  label={false}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="pie-legend">
              {question.options.map((opt, i) => (
                <div key={i} className="pie-legend-item">
                  <span
                    className="pie-legend-color"
                    style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                  />
                  <span className="pie-legend-label">{opt.label}</span>
                  <span className="pie-legend-count">{opt.count}</span>
                  <span className="pie-legend-pct">{(opt.pct || 0).toFixed(1)}%</span>
                </div>
              ))}
            </div>
            <p className="pie-multiselect-note">*choix multiples possibles</p>
          </>
        )}
      </div>
    );
  }

  if (question.type === 'flag') {
    const isPriority = question.is_priority_flag;
    const total = (question.true_count || 0) + (question.false_count || 0);
    const trueColors = isPriority ? ['#c0392b', '#e3e7ee'] : ['#2EB6A7', '#e3e7ee'];
    const chartData = [
      { name: 'Oui', count: question.true_count || 0 },
      { name: 'Non', count: question.false_count || 0 },
    ];

    return (
      <div className="pie-card">
        <h3 className="pie-title">{question.label}</h3>
        <p className="pie-meta">{total} répondus</p>
        {total > 0 && (
          <>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  innerRadius={0}
                  dataKey="count"
                  nameKey="name"
                  label={false}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={trueColors[index]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="pie-legend">
              <div className="pie-legend-item">
                <span className="pie-legend-color" style={{ backgroundColor: trueColors[0] }} />
                <span className="pie-legend-label">Oui</span>
                <span className="pie-legend-count">{question.true_count || 0}</span>
                <span className="pie-legend-pct">{((question.true_count || 0) / total * 100).toFixed(1)}%</span>
              </div>
              <div className="pie-legend-item">
                <span className="pie-legend-color" style={{ backgroundColor: trueColors[1] }} />
                <span className="pie-legend-label">Non</span>
                <span className="pie-legend-count">{question.false_count || 0}</span>
                <span className="pie-legend-pct">{((question.false_count || 0) / total * 100).toFixed(1)}%</span>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return null;
}

